/**
 * Extracts branding primitives from /device/config.branding and exposes
 * a single `applyBranding(root, branding)` function that writes CSS
 * custom properties so the rest of the player can render without
 * knowing about the config payload shape.
 *
 * Matches the Flutter Branding class at
 * apps/signage-tv/lib/rendering/branding.dart.
 */

export interface Branding {
  orgName: string;
  orgLogoUrl: string | null;
  primaryColor: string;
  secondaryColor: string | null;
  currency: string;
  locale: string;
  timezone: string;
  vocabulary: Record<string, string>;
}

export const DEFAULT_BRANDING: Branding = {
  orgName: 'Organization',
  orgLogoUrl: null,
  primaryColor: '#1F5E3E',
  secondaryColor: null,
  currency: 'USD',
  locale: 'en-US',
  timezone: 'UTC',
  vocabulary: {},
};

export function extractBranding(config: Record<string, unknown> | null | undefined): Branding {
  if (!config || typeof config !== 'object') return { ...DEFAULT_BRANDING };
  const b = (config['branding'] ?? {}) as Record<string, unknown>;
  return {
    orgName: typeof b['org_name'] === 'string' ? (b['org_name'] as string) : DEFAULT_BRANDING.orgName,
    orgLogoUrl: typeof b['org_logo_url'] === 'string' ? (b['org_logo_url'] as string) : null,
    primaryColor: normalizeHex(b['org_primary_color']) ?? DEFAULT_BRANDING.primaryColor,
    secondaryColor: normalizeHex(b['org_secondary_color']),
    currency: typeof b['currency'] === 'string' ? (b['currency'] as string) : DEFAULT_BRANDING.currency,
    locale: typeof b['locale'] === 'string' ? (b['locale'] as string) : DEFAULT_BRANDING.locale,
    timezone: typeof b['timezone'] === 'string' ? (b['timezone'] as string) : DEFAULT_BRANDING.timezone,
    vocabulary:
      b['vocabulary'] && typeof b['vocabulary'] === 'object' && !Array.isArray(b['vocabulary'])
        ? (b['vocabulary'] as Record<string, string>)
        : {},
  };
}

export function applyBranding(root: HTMLElement, branding: Branding): void {
  root.style.setProperty('--ag-primary', branding.primaryColor);
  if (branding.secondaryColor) {
    root.style.setProperty('--ag-secondary', branding.secondaryColor);
  }
}

/** Format a cents amount using the branding's currency + locale. */
export function formatCurrency(cents: number, branding: Branding): string {
  try {
    const fmt = new Intl.NumberFormat(branding.locale, {
      style: 'currency',
      currency: branding.currency,
    });
    return fmt.format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${branding.currency}`;
  }
}

function normalizeHex(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // Accept #RRGGBB, RRGGBB, #RGB, RGB. Reject anything else so broken config
  // doesn't blow up CSS.
  const cleaned = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
  if (!/^[0-9a-fA-F]{3,8}$/.test(cleaned)) return null;
  return `#${cleaned}`;
}
